import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import css from "./FileChip.module.scss";
import { Surface, SurfacePadding, SurfaceTone } from "./Surface";

interface FileChipProps {
    readonly filename: string;
    /** Optional label rendered above the filename (e.g. a model variant name). */
    readonly description?: React.ReactNode;
    /** Optional actions (e.g. buttons) rendered on the right of the chip. */
    readonly actions?: React.ReactNode;
    /** Extra class applied to the chip's root (e.g. to control sizing). */
    readonly className?: string;
    /** When set, the chip gets rounded corners. */
    readonly round?: boolean;
    /** Surface background tone (defaults to the tinted chip). */
    readonly tone?: SurfaceTone;
    /** When set, the chip reads as a raised card with a shadow. */
    readonly elevated?: boolean;
    /** When set, the chip gets a border. */
    readonly bordered?: boolean;
    /** Inner padding of the chip (defaults to small). */
    readonly padding?: SurfacePadding;
}

/**
 * A document icon alongside a filename, optionally with a description above it
 * and actions on the right. Owns its row layout so it renders correctly both
 * standalone and stacked inside a {@link CardStack}.
 */
export const FileChip: React.FC<FileChipProps> = ({
    filename,
    description,
    actions,
    className,
    round = true,
    tone = "tint",
    bordered = false,
    elevated = false,
    padding = "small"
}) => (
    <Surface
        tone={tone}
        elevated={elevated}
        radius={round ? "md" : "none"}
        bordered={bordered}
        padding={padding}
        className={classNames(css["FileChip"], className)}
    >
        <span className={css["FileChip-icon"]}>
            <Icon icon={IconNames.DOCUMENT} size={20} />
        </span>
        <div className={css["FileChip-text"]}>
            {description !== undefined && <strong>{description}</strong>}
            <span
                className={classNames(css["FileChip-name"], {
                    [css["FileChip-name--secondary"]]: description !== undefined
                })}
                title={filename}
            >
                {filename}
            </span>
        </div>
        {actions && <div className={css["FileChip-actions"]}>{actions}</div>}
    </Surface>
);
